import stripePricesDev from './stripePrices_dev.json' with { type: 'json' }
import stripePricesApp from './stripePrices_app.json' with { type: 'json' }
import { isEmpty, keyBy, uniq } from 'lodash-es'
import { getGroupPrivileges, Privileges } from './sharedUtils.js'

const stripePricesMap = {
    dev: stripePricesDev,
    app: stripePricesApp,
}

let stripePriceIdsByProductId = null

function getStripePrice(productId) {
    if (!stripePriceIdsByProductId) {
        const envName = process.env.KN_ENV === 'local'
            ? 'dev'
            : process.env.KN_ENV || 'dev'
        stripePriceIdsByProductId = stripePricesMap[envName]
        if (isEmpty(stripePriceIdsByProductId)) throw Error(`price map missing for env: ${envName}`)
    }
    return stripePriceIdsByProductId[productId]
}

class SubscriptionPlan {
    constructor({
                    name,
                    price,
                    priceLabel,
                    calcPriceLabel,
                    trialPeriodDays,
                    billingInterval,
                    monthlyTokens,
                    maxRelationships,
                    stripeProductId,
                    iosProductId,
                    androidProductId,
                    description,
                    bullets,
                    selectable = true,
                    adminSelectable = true,
                    canAddTokens = true,
                    legacy = true,
                }) {
        this.name = name
        this.price = price
        this.priceLabel = priceLabel
        this.calcPriceLabel = calcPriceLabel
        this.trialPeriodDays = trialPeriodDays
        this.billingInterval = billingInterval
        this.monthlyTokens = monthlyTokens
        this.maxRelationships = maxRelationships
        this.stripeProductId = stripeProductId
        this.iosProductId = iosProductId
        this.androidProductId = androidProductId
        this.stripePriceId = getStripePrice(stripeProductId)
        this.description = description
        this.bullets = bullets
        this.selectable = selectable
        this.adminSelectable = adminSelectable
        this.canAddTokens = canAddTokens
        this.legacy = legacy
    }
}

const FreePlan = new SubscriptionPlan({
    selectable: false,
    adminSelectable: false,
    canAddTokens: false,
    name: 'Free Plan',
    price: 0,
    priceLabel: '$0.00/mo',
    billingInterval: 'month',
    monthlyTokens: 250000,
    maxRelationships: 1,
    stripeProductId: 'prod_1kinectin4free',
    iosProductId: 'na_prod_1kinectin4free',
    androidProductId: 'play_prod_1kinectin4free',
    description: '<h3>For Testing the Waters</h3>The <bold>Free</bold> plan is for those who desire personal and relational growth, but want to try out the product before committing.',
    bullets: [
        { detail: '250K tokens per month' },
        { detail: '1 relationship' },
    ],
})

const SubscriptionPlans = [
    new SubscriptionPlan({
        name: 'Essential Monthly',
        price: 9.99,
        priceLabel: '$9.99/mo',
        trialPeriodDays: 7,
        billingInterval: 'month',
        monthlyTokens: 500000,
        maxRelationships: 5,
        stripeProductId: 'prod_RifLBAlyrnt2MV',
        iosProductId: 'com.kinectin.app.essential.mo',
        androidProductId: 'kinectin.essential.mo:play-mo',
        description: '<h3>For the Growth Seeker</h3>The <bold>Essential</bold> plan is for those eager to take the first step toward personal and relational growth. Perfect for quick, light guidance to help you reflect, this plan provides foundational coaching sessions to strengthen your connections and grow with intention. It’s an excellent starting point for building better relationships with yourself and others.',
        bullets: [
            { detail: '500K tokens per month' },
            { detail: 'Up to 5 relationships' },
            { detail: 'Buy additional tokens month to month' },
        ],
    }),
    new SubscriptionPlan({
        name: 'Plus Monthly',
        price: 19.99,
        priceLabel: '$19.99/mo',
        trialPeriodDays: 7,
        billingInterval: 'month',
        monthlyTokens: 2000000,
        maxRelationships: 5,
        stripeProductId: 'prod_RXrrXDThrPYkca',
        iosProductId: 'com.kinectin.app.plus.mo',
        androidProductId: 'kinectin.plus.mo:play-mo',
        description: '<h3>For the Consistent Improver</h3>The <bold>Plus</bold> plan is designed for those ready to invest more time in self-improvement and relationship growth. With enough sessions for regular check-ins, this tier is perfect for exploring recurring patterns, setting meaningful goals, and gaining insights into up to five relationships. It’s ideal for those who want a balance of in-depth and practical coaching sessions.',
        bullets: [
            { detail: '2M tokens per month' },
            { detail: 'Up to 5 relationships' },
            { detail: 'Buy additional tokens month to month' },
        ],
    }),
    new SubscriptionPlan({
        name: 'Advanced Monthly',
        price: 39.99,
        priceLabel: '$39.99/mo',
        trialPeriodDays: 7,
        billingInterval: 'month',
        monthlyTokens: 4250000,
        maxRelationships: 10,
        stripeProductId: 'prod_RXtsFZTsHWGOTz',
        iosProductId: 'com.kinectin.app.advanced.mo',
        androidProductId: 'kinectin.advanced.mo:play-mo',
        description: '<h3>For the Thoughtful Connector</h3>The <bold>Advanced</bold> plan is for those committed to exploring their relationships on a deeper level. Supporting up to 10 connections, this plan provides ample sessions to reflect, uncover patterns, and address challenges. It’s designed for users who are ready to dedicate time to building stronger, healthier connections with themselves and others.',
        bullets: [
            { detail: '4.25M tokens per month' },
            { detail: 'Up to 10 relationships' },
            { detail: 'Buy additional tokens month to month' },
        ],
    }),
    new SubscriptionPlan({
        legacy: false,
        name: 'Unlimited Monthly',
        price: 19.99,
        priceLabel: '$19.99/mo',
        trialPeriodDays: 7,
        billingInterval: 'month',
        monthlyTokens: null,
        maxRelationships: null,
        stripeProductId: 'prod_unlimitedpermo',
        iosProductId: 'com.kinectin.app.unlimited.mo',
        androidProductId: 'kinectin.unlimited.mo:play-mo',
        description: 'Unlimited Access is for those ready to shape their happiness with greater intention. With full access to Amari, guided activities, and personal journals, you’ll understand what’s been holding you back, and uncover the habits that help you live a healthier, more fulfilling life.',
        bullets: [
            { detail: 'Unlimited tokens' },
            { detail: 'Unlimited relationships' },
        ],
    }),


    new SubscriptionPlan({
        name: 'Essential Annually',
        price: 107.88,
        priceLabel: '$107.88/yr',
        calcPriceLabel: '$8.99/mo',
        trialPeriodDays: 7,
        billingInterval: 'year',
        monthlyTokens: 500000,
        maxRelationships: 5,
        stripeProductId: 'prod_RigCOvUA1rMoQL',
        iosProductId: 'com.kinectin.app.essential.yr',
        androidProductId: 'kinectin.essential.yr:play-yr',
        description: '<h3>For the Growth Seeker</h3>The <bold>Essential</bold> plan is for those eager to take the first step toward personal and relational growth. Perfect for quick, light guidance to help you reflect, this plan provides foundational coaching sessions to strengthen your connections and grow with intention. It’s an excellent starting point for building better relationships with yourself and others.',
        bullets: [
            { detail: '500K tokens per month' },
            { detail: 'Up to 5 relationships' },
            { detail: 'Buy additional tokens month to month' },
        ],
    }),
    new SubscriptionPlan({
        name: 'Plus Annually',
        price: 215.88,
        priceLabel: '$215.88/yr',
        calcPriceLabel: '$17.99/mo',
        trialPeriodDays: 7,
        billingInterval: 'year',
        monthlyTokens: 2000000,
        maxRelationships: 5,
        stripeProductId: 'prod_RXtsMZ8zXJzRf1',
        iosProductId: 'com.kinectin.app.plus.yr',
        androidProductId: 'kinectin.plus.yr:play-yr',
        description: '<h3>For the Consistent Improver</h3>The <bold>Plus</bold> plan is designed for those ready to invest more time in self-improvement and relationship growth. With enough sessions for regular check-ins, this tier is perfect for exploring recurring patterns, setting meaningful goals, and gaining insights into up to five relationships. It’s ideal for those who want a balance of in-depth and practical coaching sessions.',
        bullets: [
            { detail: '2M tokens per month' },
            { detail: 'Up to 5 relationships' },
            { detail: 'Buy additional tokens month to month' },
        ],
    }),
    new SubscriptionPlan({
        name: 'Advanced Annually',
        price: 431.88,
        priceLabel: '$431.88/yr',
        calcPriceLabel: '$35.99/mo',
        trialPeriodDays: 7,
        billingInterval: 'year',
        monthlyTokens: 4250000,
        maxRelationships: 10,
        stripeProductId: 'prod_RXtsWIVw2zxY7s',
        iosProductId: 'com.kinectin.app.advanced.yr',
        androidProductId: 'kinectin.advanced.yr:play-yr',
        description: '<h3>For the Thoughtful Connector</h3>The <bold>Advanced</bold> plan is for those committed to exploring their relationships on a deeper level. Supporting up to 10 connections, this plan provides ample sessions to reflect, uncover patterns, and address challenges. It’s designed for users who are ready to dedicate time to building stronger, healthier connections with themselves and others.',
        bullets: [
            { detail: '4.25M tokens per month' },
            { detail: 'Up to 10 relationships' },
            { detail: 'Buy additional tokens month to month' },
        ],
    }),
    new SubscriptionPlan({
        legacy: false,
        name: 'Unlimited Annually',
        price: 199.99,
        priceLabel: '$199.99/yr',
        calcPriceLabel: '$16.67/mo',
        trialPeriodDays: 7,
        billingInterval: 'year',
        monthlyTokens: null,
        maxRelationships: null,
        stripeProductId: 'prod_unlimitedperyr',
        iosProductId: 'com.kinectin.app.unlimited.yr',
        androidProductId: 'kinectin.unlimited.yr:play-yr',
        description: 'Unlimited Access is for those ready to shape their happiness with greater intention. With full access to Amari, guided activities, and personal journals, you’ll understand what’s been holding you back, and uncover the habits that help you live a healthier, more fulfilling life.',
        bullets: [
            { detail: 'Unlimited tokens' },
            { detail: 'Unlimited relationships' },
        ],
    }),
    new SubscriptionPlan({
        selectable: false,
        adminSelectable: false,
        canAddTokens: false,
        name: 'Beta (3 days)',
        price: 0,
        priceLabel: '$0.00',
        billingInterval: '3-day',
        monthlyTokens: 500000,
        maxRelationships: 5,
        stripeProductId: 'prod_KnBeTA3DaYs5rL',
        iosProductId: 'na_prod_KnBeTA3DaYs5rL',
        androidProductId: 'play_prod_KnBeTA3DaYs5rL',
        description: '<h3>For the Trailblazing Connectors</h3>As an early Kinectin user, you’re helping shape the future of relationship support. This limited-time beta plan gives you full access to Amari, our AI-powered coach, as well as our relationship assessments, providing personalized insights and guidance tailored to your journey. Your experience and feedback will help us refine Kinectin as a whole—from Amari’s coaching to the tools that support deeper connection. Thank you for being part of this mission—we’re building Kinectin together!',
        bullets: [
            { detail: '500K tokens for 3 days' },
            { detail: '5 relationships' },
        ],
    }),
    new SubscriptionPlan({
        selectable: false,
        name: 'Friends & Family',
        price: 0,
        priceLabel: '$0.00/mo',
        billingInterval: 'month',
        monthlyTokens: 3000000,
        maxRelationships: 10,
        stripeProductId: 'prod_FRIENDSNFAMILY',
        iosProductId: 'na_prod_FRIENDSNFAMILY',
        androidProductId: 'play_prod_FRIENDSNFAMILY',
        description: '<h3>For Our Inner Circle</h3>The <bold>Friends & Family</bold> plan is for those who have been part of our journey from the start—our closest supporters, our biggest believers, and those who inspire us to keep building. As part of our inner circle, you’ll receive full access to Kinectin’s coaching, assessments, and insights to help you strengthen your most important relationships. Your trust means everything to us, and this plan is our way of saying thank you for being part of the Kinectin family.',
        bullets: [
            { detail: '3M tokens per month' },
            { detail: '10 relationships' },
            { detail: 'Buy additional tokens month to month' },
        ],
    }),
    FreePlan,
]

const SubscriptionPlansByPriceId = keyBy(SubscriptionPlans, 'stripePriceId')
const SubscriptionPlansByProductId = Object.assign(
    keyBy(SubscriptionPlans, 'stripeProductId'),
    keyBy(SubscriptionPlans, 'iosProductId'),
    keyBy(SubscriptionPlans, 'androidProductId'),
)

class OneTimePurchase {
    constructor({
                    name,
                    price,
                    priceLabel,
                    monthlyTokens,
                    stripeProductId,
                    iosProductId,
                    androidProductId,
                    description,
                    bullets,
                }) {
        this.name = name
        this.price = price
        this.priceLabel = priceLabel
        this.monthlyTokens = monthlyTokens
        this.stripeProductId = stripeProductId
        this.iosProductId = iosProductId
        this.stripePriceId = getStripePrice(stripeProductId)
        this.androidProductId = androidProductId
        this.description = description
        this.bullets = bullets
    }
}

const OneTimePurchases = [
    new OneTimePurchase({
        name: 'Token Up',
        price: 9.99,
        priceLabel: '$9.99',
        monthlyTokens: 500000,
        stripeProductId: 'prod_RXtssiz8U5TtrW',
        iosProductId: 'com.kinectin.app.tokenup.ot',
        androidProductId: 'kinectin.tokenup.ot.play',
        description: '<h3>Keep the Conversation Going</h3>Ran out of tokens? No problem. With Token Ups, you can purchase additional tokens anytime, ensuring uninterrupted access to Amari’s coaching and insights. Whether you need a quick check-in or an in-depth session, topping up allows you to continue your journey without limits. Your relationships deserve continuous support—stay connected, stay growing.',
        bullets: [
            {
                icon: 'mdi-creation',
                iconColor: 'primary',
                detail: 'A single purchase, valid for the current billing cycle',
            },
        ],
    }),
]

const OneTimePurchasesByPriceId = keyBy(OneTimePurchases, 'stripePriceId')
const OneTimePurchasesByProductId = Object.assign(
    keyBy(OneTimePurchases, 'stripeProductId'),
    keyBy(OneTimePurchases, 'iosProductId'),
    keyBy(OneTimePurchases, 'androidProductId'),
)


function isPlanDowngrade(status, productId, nextProductId) {
    if (status !== 'active') return false
    const existingPlan = SubscriptionPlansByProductId[productId] || getDefaultSubscriptionPlan()
    const nextPlan = SubscriptionPlansByProductId[nextProductId]
    if (existingPlan.price === 0 || nextPlan.price === 0) return false
    if (existingPlan.billingInterval === 'year' && nextPlan.billingInterval === 'month') return true
    if (existingPlan.billingInterval === 'month' && nextPlan.billingInterval === 'year') return false
    return existingPlan.price > nextPlan.price
}

function getDefaultSubscriptionPlan() {
    return FreePlan
}

const UnlimitedMonthlyStripeProductId = 'prod_unlimitedpermo'
const UnlimitedAnnuallyStripeProductId = 'prod_unlimitedperyr'

export {
    SubscriptionPlans,
    SubscriptionPlansByPriceId,
    SubscriptionPlansByProductId,
    isPlanDowngrade,
    OneTimePurchases,
    OneTimePurchasesByPriceId,
    OneTimePurchasesByProductId,
    getDefaultSubscriptionPlan,
    FreePlan,
    UnlimitedMonthlyStripeProductId,
    UnlimitedAnnuallyStripeProductId,
}